import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import { usePaymentCharges } from "services/hooks/rentals";

const CHARGE_SCOPE = "ANONYMOUS_TENANCY";

const PaymentCharges = () => {
  const role = useSelector((state) => state.role);
  const {
    paymentCharges,
    isLoadingPaymentCharges,
    errorPaymentCharges,
    isSubmittingPaymentCharge,
    paymentChargeActionError,
    createPaymentCharge,
    updatePaymentCharge,
    deletePaymentCharge,
  } = usePaymentCharges();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [form, setForm] = useState({
    name: "",
    scope: CHARGE_SCOPE,
    charge_type: "FLAT",
    charge_value: "",
    is_active: true,
  });

  const list = Array.isArray(paymentCharges) ? paymentCharges : [];

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm({
      name: "",
      scope: CHARGE_SCOPE,
      charge_type: "FLAT",
      charge_value: "",
      is_active: true,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      scope: row.scope || CHARGE_SCOPE,
      charge_type: row.charge_type || "FLAT",
      charge_value: row.charge_value ?? "",
      is_active: row.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    const parsedChargeValue = Number(form.charge_value);
    if (!form.name.trim()) {
      setMessage({ type: "error", text: "Charge name is required." });
      return;
    }
    if (Number.isNaN(parsedChargeValue) || parsedChargeValue < 0) {
      setMessage({ type: "error", text: "Charge value must be a valid positive number." });
      return;
    }

    const payload = {
      name: form.name.trim(),
      scope: CHARGE_SCOPE,
      charge_type: form.charge_type,
      charge_value: parsedChargeValue,
      is_active: form.is_active,
    };

    const response = editingId
      ? await updatePaymentCharge(editingId, payload)
      : await createPaymentCharge(payload);

    if (response.success) {
      setMessage({
        type: "success",
        text: editingId ? "Charge config updated successfully." : "Charge config created successfully.",
      });
      setDialogOpen(false);
    } else {
      setMessage({ type: "error", text: response.message || "Failed to save charge config." });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this charge config?")) return;
    const response = await deletePaymentCharge(id);
    if (response.success) {
      setMessage({ type: "success", text: "Charge config deleted successfully." });
    } else {
      setMessage({ type: "error", text: response.message || "Failed to delete charge config." });
    }
  };

  if (role !== "admin") {
    return (
      <Box m={2}>
        <Alert severity="warning">Only admins can access Payment Charge Configuration.</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Payment Charge Configuration</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Add Charge
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure additional charges for anonymous tenancies. Current scope supported by backend is
        {" "}
        <strong>{CHARGE_SCOPE}</strong>.
      </Typography>

      {message.text && (
        <Alert
          severity={message.type === "error" ? "error" : "success"}
          sx={{ mb: 2 }}
          onClose={() => setMessage({ type: "", text: "" })}
        >
          {message.text}
        </Alert>
      )}

      {paymentChargeActionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {paymentChargeActionError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingPaymentCharges ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : errorPaymentCharges ? (
          <Alert severity="error">Failed to load charge configurations.</Alert>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>No charge configurations found.</TableCell>
                </TableRow>
              ) : (
                list.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.name || "—"}</TableCell>
                    <TableCell>{row.scope || CHARGE_SCOPE}</TableCell>
                    <TableCell>{row.charge_type || "—"}</TableCell>
                    <TableCell>{row.charge_value ?? 0}</TableCell>
                    <TableCell>{row.is_active ? "Active" : "Inactive"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Edit Charge Configuration" : "Create Charge Configuration"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Charge Name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            margin="normal"
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Scope</InputLabel>
            <Select
              label="Scope"
              value={form.scope}
              onChange={(event) => setForm((prev) => ({ ...prev, scope: event.target.value }))}
              disabled
            >
              <MenuItem value={CHARGE_SCOPE}>{CHARGE_SCOPE}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Charge Type</InputLabel>
            <Select
              label="Charge Type"
              value={form.charge_type}
              onChange={(event) => setForm((prev) => ({ ...prev, charge_type: event.target.value }))}
            >
              <MenuItem value="FLAT">FLAT</MenuItem>
              <MenuItem value="PERCENTAGE">PERCENTAGE</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="number"
            label="Charge Value"
            value={form.charge_value}
            onChange={(event) => setForm((prev) => ({ ...prev, charge_value: event.target.value }))}
            margin="normal"
          />

          <Box display="flex" alignItems="center" mt={1}>
            <Switch
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
            />
            <Typography variant="body2">Active</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isSubmittingPaymentCharge}>
            {isSubmittingPaymentCharge ? <CircularProgress size={24} /> : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentCharges;
